import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Globe,
  CheckCircle2,
  XCircle,
  ExternalLink,
  FileText,
  Search,
  Smartphone,
  Code,
  ArrowLeft,
  Copy,
  Check,
  Download,
  Loader2,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import api from '../services/api';

const Pill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      ok
        ? 'bg-green-100 text-green-800 border border-green-200'
        : 'bg-gray-100 text-gray-500 border border-gray-200'
    }`}
    data-testid={`lang-pill-${label}-${ok ? 'ok' : 'missing'}`}
  >
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label.toUpperCase()}
  </span>
);

const IconLink = ({ href, title, icon: Icon, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    data-testid={testid}
    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 hover:border-capsule-teal hover:bg-capsule-teal/10 text-gray-600 hover:text-capsule-teal transition-all"
  >
    <Icon className="w-4 h-4" />
  </a>
);

const AdminSEOPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [filter, setFilter] = useState('all'); // all | incomplete
  const [downloading, setDownloading] = useState(false);

  const downloadHandbook = async () => {
    try {
      setDownloading(true);
      const res = await api.get('/admin/seo/handbook.pdf', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `penlab-access-handbook-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download failed: ${err?.response?.status || err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [s, c] = await Promise.all([
          api.get('/admin/seo/summary'),
          api.get('/admin/seo/coverage'),
        ]);
        setSummary(s.data);
        setRows(c.data.products || []);
      } catch (err) {
        if (err?.response?.status === 403) {
          navigate('/');
          return;
        }
        setError(err.message || 'Failed to load SEO dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // noop
    }
  };

  const visible =
    filter === 'incomplete'
      ? rows.filter((r) => !(r.coverage.en && r.coverage.es && r.coverage.ru))
      : rows;

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center text-gray-500">
          Loading SEO dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center text-red-600">
          {error}
        </div>
      </div>
    );
  }

  const pct = (n) =>
    summary?.total_products
      ? Math.round((n / summary.total_products) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-capsule-bg" data-testid="admin-seo-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <Link
          to="/admin"
          className="inline-flex items-center text-gray-600 hover:text-capsule-teal font-medium text-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-2xl flex items-center justify-center">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            SEO & Rich Results
          </h1>
        </div>
        <p className="text-gray-600 mb-8">
          Per-product SEO coverage, sitemap/feed URLs, and one-click validation
          in Google & Yandex search tools.
        </p>

        {/* Coverage cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {['en', 'es', 'ru', 'all_three'].map((k) => {
            const n = summary?.coverage?.[k] || 0;
            const total = summary?.total_products || 0;
            const label =
              k === 'all_three' ? 'Full EN+ES+RU' : k.toUpperCase();
            return (
              <div
                key={k}
                className="glass-card rounded-2xl p-5"
                data-testid={`coverage-card-${k}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-500 font-medium">{label}</span>
                  <span className="text-xs text-gray-400">{pct(n)}%</span>
                </div>
                <div className="text-3xl font-bold text-gray-900 mt-1">
                  {n}
                  <span className="text-base font-normal text-gray-400">
                    /{total}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-capsule-teal to-capsule-aqua"
                    style={{ width: `${pct(n)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Access & Credentials Handbook, one-click download */}
        <div className="glass-card rounded-2xl p-6 mb-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50/60 to-white" data-testid="handbook-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4 min-w-0 flex-1">
              <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  Access &amp; Credentials Handbook
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  Complete PDF of every URL, role, credential, webhook, payment provider, and recovery procedure for the PenLab Peptide platform. Regenerated on every download.
                </p>
                <p className="text-xs text-amber-700 font-medium mt-1.5">
                  ⚠ Highly confidential, store in your password vault, never share publicly.
                </p>
              </div>
            </div>
            <button
              onClick={downloadHandbook}
              disabled={downloading}
              data-testid="download-handbook-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-md whitespace-nowrap"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Feed + submission toolbox */}
        <div className="glass-card rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-capsule-teal" /> Feeds & sitemap
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary &&
              Object.entries(summary.feeds).map(([key, url]) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/60 border border-gray-200"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase text-gray-500 font-medium">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm text-gray-800 font-mono truncate">
                      {url}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => copy(url, key)}
                      title="Copy URL"
                      data-testid={`copy-${key}`}
                      className="w-8 h-8 rounded-lg border border-gray-200 hover:border-capsule-teal hover:bg-capsule-teal/10 flex items-center justify-center text-gray-600 hover:text-capsule-teal transition-all"
                    >
                      {copied === key ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open"
                      className="w-8 h-8 rounded-lg border border-gray-200 hover:border-capsule-teal hover:bg-capsule-teal/10 flex items-center justify-center text-gray-600 hover:text-capsule-teal transition-all"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
          </div>

          <div className="mt-6 pt-5 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-3">
            <a
              href={summary?.submit_links?.google_search_console}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium hover:opacity-90 transition"
              data-testid="submit-gsc"
            >
              <Search className="w-4 h-4" /> Google Search Console
            </a>
            <a
              href={summary?.submit_links?.google_merchant_center}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium hover:opacity-90 transition"
              data-testid="submit-gmc"
            >
              <FileText className="w-4 h-4" /> Google Merchant Center
            </a>
            <a
              href={summary?.submit_links?.yandex_webmaster}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white text-sm font-medium hover:opacity-90 transition"
              data-testid="submit-yandex"
            >
              <Globe className="w-4 h-4" /> Yandex Webmaster
            </a>
          </div>
        </div>

        {/* Product table */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Products ({rows.length})
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  filter === 'all'
                    ? 'bg-capsule-teal text-white'
                    : 'bg-white/60 border border-gray-200 text-gray-700 hover:border-capsule-teal'
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              <button
                onClick={() => setFilter('incomplete')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  filter === 'incomplete'
                    ? 'bg-capsule-teal text-white'
                    : 'bg-white/60 border border-gray-200 text-gray-700 hover:border-capsule-teal'
                }`}
                data-testid="filter-incomplete"
              >
                Incomplete only
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="seo-products-table">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                  <th className="py-2.5 px-2 font-medium">Product</th>
                  <th className="py-2.5 px-2 font-medium">Languages</th>
                  <th className="py-2.5 px-2 font-medium">Validate</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-100 hover:bg-white/50 transition"
                    data-testid={`row-${r.id}`}
                  >
                    <td className="py-3 px-2">
                      <div className="font-medium text-gray-900 truncate max-w-xs">
                        {r.name_en}
                      </div>
                      <a
                        href={r.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-capsule-teal hover:underline font-mono"
                      >
                        {r.product_url.replace(/^https?:\/\//, '')}
                      </a>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex gap-1.5 flex-wrap">
                        <Pill ok={r.coverage.en} label="en" />
                        <Pill ok={r.coverage.es} label="es" />
                        <Pill ok={r.coverage.ru} label="ru" />
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex gap-1.5">
                        <IconLink
                          href={r.validation.google_rich_results}
                          title="Google Rich Results Test"
                          icon={Code}
                          testid={`test-rich-${r.id}`}
                        />
                        <IconLink
                          href={r.validation.google_mobile_friendly}
                          title="Google Mobile-Friendly Test"
                          icon={Smartphone}
                          testid={`test-mobile-${r.id}`}
                        />
                        <IconLink
                          href={r.validation.schema_validator}
                          title="Schema.org Validator"
                          icon={FileText}
                          testid={`test-schema-${r.id}`}
                        />
                        <IconLink
                          href={r.validation.yandex_rich_snippet}
                          title="Yandex Rich-Snippet Test"
                          icon={Globe}
                          testid={`test-yandex-${r.id}`}
                        />
                        <IconLink
                          href={r.validation.view}
                          title="Open product page"
                          icon={ExternalLink}
                          testid={`open-${r.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visible.length === 0 && (
              <div className="text-center py-10 text-gray-500">
                No products match the current filter.
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminSEOPage;
